import { PublicRoutes } from "@/constants/routes";
import { Activity, CalendarClock, Headset, Wrench } from "lucide-react";
import type { LegalDocument } from "./types";

export const AVAILABILITY: LegalDocument = {
  key: "disponibilidade",
  path: PublicRoutes.DISPONIBILIDADE,
  eyebrow: "Nível de serviço",
  title: "Disponibilidade e suporte",
  seoTitle: "Disponibilidade e suporte da plataforma",
  description:
    "O nível de serviço da Fidexa: meta de disponibilidade mensal, como ela é medida, manutenções programadas, o que fica de fora e os horários e prazos do suporte.",
  intro:
    "Este é o resumo, em linguagem simples, do nível de serviço previsto no contrato da Fidexa. Se houver diferença entre esta página e o contrato assinado, vale o contrato.",
  version: "1.0",
  updatedAt: "2026-10-12",

  highlights: [
    {
      icon: Activity,
      title: "Meta de 90% ao mês",
      text: "Empregamos esforços comercialmente razoáveis para manter a plataforma disponível em 90% do tempo, medido mês a mês.",
    },
    {
      icon: Wrench,
      title: "Manutenção avisada",
      text: "Manutenção programada é avisada com 48 horas de antecedência, de preferência de madrugada, em fim de semana ou feriado.",
    },
    {
      icon: Headset,
      title: "Suporte em dias úteis",
      text: "De segunda a sexta, das 9h às 18h (horário de Brasília), com primeira resposta em até 24 horas úteis.",
    },
    {
      icon: CalendarClock,
      title: "Mudanças com 30 dias",
      text: "Mudanças nas funcionalidades e nas condições do contrato são avisadas com 30 dias de antecedência.",
    },
  ],

  sections: [
    {
      id: "meta",
      title: "Meta de disponibilidade",
      summary:
        "Nos esforçamos para que a plataforma fique disponível em pelo menos 90% do tempo de cada mês.",
      blocks: [
        {
          type: "paragraph",
          text: 'A Fidexa emprega esforços comercialmente razoáveis para manter a plataforma disponível em um patamar de **90% do tempo**, medido mensalmente ("uptime"). Esse é o compromisso do contrato de licenciamento.',
        },
        {
          type: "paragraph",
          text: "Como a cobrança depende de serviços de terceiros, como o WhatsApp, e de integrações com os sistemas da loja, a disponibilidade da plataforma não garante a entrega de cada mensagem nem o resultado da cobrança. A recuperação dos valores depende de fatores fora da plataforma, como a situação financeira dos clientes da loja.",
        },
      ],
    },
    {
      id: "como-medimos",
      title: "Como medimos",
      summary:
        "Disponibilidade é o tempo do mês em que a plataforma funcionou, descontadas as situações que o contrato exclui.",
      blocks: [
        {
          type: "paragraph",
          text: "A disponibilidade do mês é calculada assim: o total de minutos do mês, menos os minutos em que a plataforma ficou indisponível, dividido pelo total de minutos do mês. Não entram nessa conta as indisponibilidades listadas na seção [O que fica de fora](#exclusoes).",
        },
      ],
    },
    {
      id: "manutencao",
      title: "Manutenção programada",
      summary:
        "Avisamos com pelo menos 48 horas de antecedência e, sempre que possível, fazemos de madrugada, em fim de semana ou feriado.",
      blocks: [
        {
          type: "paragraph",
          text: "Manutenções programadas são comunicadas à loja com no mínimo **48 horas** de antecedência e realizadas, de preferência, entre 0h e 6h (horário de Brasília), em fins de semana ou feriados. O tempo de manutenção programada não conta como indisponibilidade.",
        },
      ],
    },
    {
      id: "exclusoes",
      title: "O que fica de fora",
      summary:
        "Não contam como indisponibilidade: manutenção avisada, força maior, falhas de serviços de terceiros e problemas causados pelo uso da conta da loja.",
      blocks: [
        {
          type: "list",
          items: [
            "**Manutenção programada**, avisada com 48 horas de antecedência.",
            "**Força maior:** fatos fora do controle razoável da Fidexa, como ataques cibernéticos massivos (DDoS) ou atos de autoridades públicas.",
            "**Serviços de terceiros:** falhas, bloqueios ou mudanças de política de hospedagem, telecomunicações ou APIs de parceiros, como o WhatsApp da Meta e as integrações com ERPs.",
            "**Ações da loja:** mau uso, violação do contrato ou falhas nos equipamentos, na internet ou nos sistemas da própria loja.",
          ],
        },
      ],
    },
    {
      id: "infraestrutura",
      title: "Nossa infraestrutura",
      summary:
        "A plataforma roda na Hostinger, com cópias de segurança na Microsoft Azure. Os compromissos desses fornecedores são com a Fidexa, não substituem o nosso.",
      blocks: [
        {
          type: "paragraph",
          text: "A plataforma usa fornecedores de infraestrutura reconhecidos: a Hostinger, para os servidores e o banco de dados, e a Microsoft Azure, para as cópias de segurança. Esses fornecedores oferecem à Fidexa compromissos próprios de disponibilidade. Eles são do fornecedor perante a Fidexa e não constituem compromisso da Fidexa perante a loja: o nível de serviço da Fidexa é exclusivamente o descrito nesta página e no contrato.",
        },
        {
          type: "paragraph",
          text: `Veja como protegemos os dados na página [Segurança](${PublicRoutes.SEGURANCA}).`,
        },
      ],
    },
    {
      id: "suporte",
      title: "Suporte",
      summary:
        "Atendemos de segunda a sexta, das 9h às 18h, e respondemos o primeiro contato em até 24 horas úteis.",
      blocks: [
        {
          type: "table",
          caption: "Como funciona o suporte",
          columns: [
            { key: "item", header: "Item", primary: true },
            { key: "como", header: "Como funciona" },
          ],
          rows: [
            {
              item: "Horário",
              como: "Segunda a sexta-feira, das 9h às 18h (horário de Brasília), exceto feriados.",
            },
            {
              item: "Primeira resposta",
              como: "Em até 24 horas úteis após a abertura do chamado.",
            },
            {
              item: "Canais",
              como: "E-mail e WhatsApp, indicados no contrato da loja.",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Para resolver mais rápido, conte o que aconteceu, em qual loja e, se possível, envie um print da tela.",
        },
      ],
    },
    {
      id: "bloqueio",
      title: "Suspensão do acesso",
      summary:
        "O acesso só é suspenso em casos previstos no contrato, como fraude, risco à segurança ou atraso no pagamento acima de 15 dias.",
      blocks: [
        {
          type: "list",
          items: [
            "**Justa causa:** em caso de fraude, violação de segurança ou compartilhamento indevido de credenciais que coloque a plataforma ou terceiros em risco, o acesso pode ser bloqueado temporariamente. A loja é avisada na hora e recebe a comprovação em até 24 horas; sem ela, o bloqueio é levantado.",
            "**Atraso no pagamento:** atraso acima de 15 dias autoriza a suspensão, mediante aviso, até a quitação.",
          ],
        },
      ],
    },
  ],

  changelog: [
    {
      version: "1.0",
      date: "2026-10-12",
      changes: ["Primeira versão publicada."],
    },
  ],
};
